import ResourcesIds from "../resources/ResourcesIds";
import { UserRole } from "../types";
import { ToolbarButton } from "./models/toolbarButtons";

/**
 * The roles that take part in appointments. Everything these can reach is tenant scoped,
 * so a platform session -- which names no clinic -- would be refused by the API. A clinic's
 * admin runs the place rather than attends it, so the dashboard and the booking pages are
 * not theirs either: their console is.
 *
 * Both roles appear on an appointment, but from opposite ends, which is why this is now
 * narrower than it looks: it gates My Appointments and nothing else. See customerRoles.
 */
export const careRoles: UserRole[] = ['THERAPIST', 'CUSTOMER'];

/**
 * The person who attends appointments rather than gives them. Booking is theirs, and so
 * is the dashboard that fronts it -- a therapist does not book themselves a session, and
 * showing them the customer's version of the app was only ever an accident of both roles
 * sharing one gate.
 */
export const customerRoles: UserRole[] = ['CUSTOMER'];

/** The person who gives the appointments. Their working view is the patient list. */
export const therapistRoles: UserRole[] = ['THERAPIST'];

export const superAdminRoles: UserRole[] = ['SUPER_ADMIN'];

/** A clinic's own administrator, as opposed to the platform owner above them. */
export const adminRoles: UserRole[] = ['ADMIN'];

/**
 * Who may open one patient's file. The therapist works from it; the administrator reaches it
 * from My Clinic, chiefly to delete an evaluation that should not be there -- which is theirs
 * alone to do. The patient list and the intake wizard stay therapist-only.
 */
export const patientFileRoles: UserRole[] = [...therapistRoles, ...adminRoles];

/**
 * Everyone except the platform owner. Our Services is a marketing page for a clinic's
 * audience -- customers, therapists, and the clinic admins who run a practice -- not for
 * SUPER_ADMIN, who owns the platform rather than a clinic and has no use for it.
 */
export const nonSuperAdminRoles: UserRole[] = ['ADMIN', 'THERAPIST', 'CUSTOMER'];

/**
 * Who About is for: customers deciding whether to book, and the clinic admins who run the
 * practice it describes. A therapist already works there -- being pitched the clinic they
 * staff doesn't make sense the way it does for Our Services, so THERAPIST is left out here.
 */
export const aboutRoles: UserRole[] = ['ADMIN', 'CUSTOMER'];

/**
 * The platform's own mark, and what everything falls back to.
 *
 * Carefull rather than a clinic, deliberately. One static build serves every clinic and a
 * signed-out visitor has not said which one they belong to, so anything drawn before sign-in
 * has to be the platform's -- dressing it as whichever clinic happens to be tenant 1 would
 * show every other clinic's patients a competitor's front door.
 *
 * Once a session names a clinic, `BrandingContext` supplies that clinic's own logo and these
 * become the fallback for a clinic that has not uploaded one.
 */
export const appName: string = "Carefull";
export const logoAlt: string = appName;
export const logoSrc: string = "/images/carefull-header-logo.png";
/** The taller lockup with the slogan, for the access and sign-in pages. */
export const logoWithSloganSrc: string = "/images/carefull-logo-slogan.png";
export const dashboardLink: string = '/dashboard';
export const bookAppointmentLink: string = '/appointments';
export const myAppointmentsLink: string = '/my-appointments';
export const aboutLink: string = '/about';
export const loginLink: string = '/login';
export const registerLink: string = '/register';
/**
 * Whether a visitor may create their own account. Off, because there is no self-registration
 * yet: accounts are invited -- a therapist adds their patients, an admin their therapists --
 * and the backend has no POST /auth/register, so the form could only ever answer an error.
 *
 * Hidden rather than removed, because self-registration is planned. RegisterForm,
 * RegisterConfirmation and authService.registerMutation all stay, and every way in reads this
 * one switch: the header button, the link under the sign-in form, the landing page's two
 * calls to action, and the routes themselves. Turning it back on is this line plus the
 * endpoint on the backend.
 */
export const selfRegistrationEnabled: boolean = false;
export const profileLink: string = '/profile';
export const termsAndConditionsLink: string = '/terms-and-conditions';
export const registerConfirmationLink: string = '/register-confirmation';
export const forgotPasswordLink: string = '/forgot-password';
export const clinicsLink: string = '/clinics';
/** Not '/clinic': one character away from the platform owner's '/clinics' reads as a typo. */
export const clinicAdminLink: string = '/clinic-admin';
/**
 * The clinic admin's numbers, as opposed to clinicAdminLink above, which is its people.
 *
 * Two pages rather than a tab on one, because they answer different questions and get
 * opened for different reasons: an admin looks at this one most mornings and at the
 * console only when somebody joins or leaves. Not '/dashboard', which is the customer's
 * and gated to them.
 */
export const clinicDashboardLink: string = '/clinic-dashboard';
export const patientsLink: string = '/patients';
/**
 * The intake wizard. Under /patients because that is where a therapist starts from and
 * where they land again afterwards -- an evaluation is how a new patient joins the list.
 */
export const newEvaluationLink: string = '/patients/new-evaluation';
/**
 * One patient's file: what they bought, what is left of it, and their evaluations.
 *
 * The route is '/patients/:patientId', so it shares a prefix with newEvaluationLink
 * above. React Router ranks a static segment above a dynamic one, so '/patients/new-
 * evaluation' still wins over an id of "new-evaluation" -- but the two must stay in this
 * order in App.tsx for a reader to see why.
 */
export const patientDetailLinkFor = (patientId: number): string => `/patients/${patientId}`;
/**
 * The same wizard as newEvaluationLink, opened for somebody already on the list: a
 * returning patient's next sheet rather than a stranger's first one.
 *
 * A patient accumulates evaluations for as long as they are treated, and the only way to
 * record the second one used to be to leave their file, start a blank form and retype
 * their address -- where a typo silently invents a second patient, since the address is
 * what binds a sheet to an account.
 */
export const followUpEvaluationLinkFor = (patientId: number): string =>
    `/patients/${patientId}/new-evaluation`;
/**
 * A therapist's own money: what they invoiced, and what they actually provided.
 *
 * '/my-income' rather than a second '/dashboard' path, because a therapist has no clinic
 * dashboard to be a variant of -- the two pages answer to different roles and only one of
 * them is ever reachable from a given session.
 */
export const therapistIncomeLink: string = '/my-income';
/** The therapist's own diary. One column: they never see a colleague's. */
export const myCalendarLink: string = '/my-calendar';

/**
 * The therapist's own working week, as opposed to myCalendarLink above, which is what is in
 * it. Its own page rather than a panel on the diary: this is a setting somebody changes twice
 * a year, and the diary is what they open every morning.
 *
 * Deliberately **not** '/my-schedule', which the page is called in every language. That path
 * is already taken -- it is what the diary used to be called, and App.tsx still redirects it
 * there for the therapists who bookmarked it. Reclaiming it would send exactly those people
 * somewhere they did not mean to go, silently, and the two pages are close enough in subject
 * that they would not immediately notice. So the path follows the clinic-side tab it mirrors
 * instead, and the visible name follows the therapist's own words for it.
 */
export const myScheduleLink: string = '/my-working-hours';

/**
 * Router state that opens My Calendar with its new-entry dialog already up.
 *
 * My Appointments is shared by both ends of a booking, but only one end can book from
 * it: '/appointments' is gated to customerRoles, so a therapist pressing the same button
 * was bounced straight back out by the guard. A therapist creates an appointment in their
 * own diary instead, and this carries the press across so they land on the dialog rather
 * than on the diary with the work still to do.
 *
 * State rather than a query string: it is a one-shot instruction, not a place. My Calendar
 * consumes it and replaces the history entry, so a refresh or a Back into it shows the
 * plain diary.
 */
export interface CalendarLocationState {
    openNewEntry?: boolean;
}


/**
 * Where a session belongs when it has nowhere else to go: after signing in, behind the
 * logo, and whenever a guard turns it away. The platform owner names no clinic, the
 * clinic admin does not attend appointments, and a therapist gives them rather than
 * books them, so only a customer lands on the customer dashboard.
 *
 * A therapist lands on their own diary: they open the app to see who they are treating
 * today. The patient list is one nav entry away, for the days they come to look someone up.
 *
 * The clinic admin lands on their own dashboard rather than on their console: the reason
 * to open the app most mornings is to see how the month is going, not to invite a
 * therapist.
 */
export const landingLinkFor = (role: UserRole | null): string => {
    if (role === 'SUPER_ADMIN') {
        return clinicsLink;
    }

    if (role === 'ADMIN') {
        return clinicDashboardLink;
    }

    if (role === 'THERAPIST') {
        return myCalendarLink;
    }

    return dashboardLink;
};


export const toolbarButtons: ToolbarButton[] = [
    // First, and the therapist's landing page: they open the app to see who they are
    // treating today. The count of booking requests waiting for their answer used to be drawn
    // on this entry; it is on the bell in the header now, beside the rest of their news.
    { text: ResourcesIds.MY_CALENDAR_NAV, link: myCalendarLink, roles: therapistRoles },
    { text: ResourcesIds.PATIENTS, link: patientsLink, roles: therapistRoles },
    // Straight after, for the same reason the clinic dashboard sits before My Clinic: the
    // numbers are what the month is judged on. It is not the therapist's landing page
    // though -- they open the app to see who they are treating today, not what they earned.
    { text: ResourcesIds.THERAPIST_INCOME_NAV, link: therapistIncomeLink, roles: therapistRoles },
    // Last of the therapist's four: the hours they keep change twice a year, where the three
    // above are opened daily. Still a nav entry rather than a corner of the diary, because
    // nothing else on the app would ever lead them to it.
    { text: ResourcesIds.MY_SCHEDULE_NAV, link: myScheduleLink, roles: therapistRoles },
    { text: ResourcesIds.DASHBOARD, link: dashboardLink, roles: customerRoles },
    { text: ResourcesIds.BOOK_APPOINTMENT, link: bookAppointmentLink, roles: customerRoles },
    // careRoles rather than customerRoles, deliberately: it is the one appointments page
    // that reads the same from either end of the booking, and the API answers it from
    // whichever end the caller is on.
    { text: ResourcesIds.MY_APPOINTMENTS, link: myAppointmentsLink, roles: careRoles },
    { text: ResourcesIds.CLINICS, link: clinicsLink, roles: superAdminRoles },
    // Before My Clinic, and for the same reason it is the admin's landing: the numbers
    // are what the day starts with, the console is where they go when somebody joins.
    { text: ResourcesIds.CLINIC_DASHBOARD_NAV, link: clinicDashboardLink, roles: adminRoles },
    { text: ResourcesIds.MY_CLINIC, link: clinicAdminLink, roles: adminRoles },
    { text: ResourcesIds.OUR_SERVICES_TITLE, link: '/ourServices', roles: nonSuperAdminRoles },
    { text: ResourcesIds.ABOUT, link: aboutLink, roles: aboutRoles }
];

/**
 * A person's name. The range reaches Latin Extended-B rather than stopping at Latin-1,
 * because Romanian's own letters live past it: 'ș' and 'ț' are U+0219 and U+021B, so an
 * 'À-ÿ' class accepts Jérôme and refuses Ștefan -- in a Romanian-first app. The two gaps
 * skip the multiplication and division signs, which Latin-1 files among the letters.
 */
export const nameRegEx: RegExp = /^[A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u024F' -]+$/;